import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  InfiniteRowModelModule,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useState, useMemo, useCallback } from "react";
import CustomHeader from "../../CustomHeader";
import ActionCellBilling from "../Utility/ActionCellBilling";
import ActionCellDistributor from "../Utility/ActionCellDistributor";

ModuleRegistry.registerModules([InfiniteRowModelModule]);

function DistributorTable({
  // keeping original props signature for drop-in compatibility
  getPatients,
  isFetching,
  isLoading,
  isSuccess,
  gridRef,
  selectedFilters,
  hasData,
  setHasData,
}) {
  const navigate = useNavigate();

  const getStableColor = (key) => {
    const bgColors = [
      "bg-danger bg-opacity-50",
      "bg-success bg-opacity-50",
      "bg-primary bg-opacity-50",
      "bg-warning bg-opacity-50",
      "bg-secondary bg-opacity-50",
      "bg-danger bg-opacity-50",
      "bg-primary bg-opacity-50",
      "bg-info bg-opacity-50",
      "bg-warning bg-opacity-50",
      "bg-success bg-opacity-50",
      "bg-info bg-opacity-10",
      "bg-success bg-opacity-50",
      "bg-danger bg-opacity-50",
      "bg-danger bg-opacity-50",
      "bg-secondary bg-opacity-50",
    ];
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % bgColors.length;
    return bgColors[index];
  };

  // Updated static demo rows to match distributor data structure
  const staticRows = useMemo(
    () => [
      {
        distributorName: "Medicore Pharmaceuticals",
        distributorAddress: "123 Medical Plaza, Mumbai, Maharashtra 400001",
        gstin: "27ABCDE1234F1Z5",
        contact: "+91 98765 43210",
        credit: "₹2,50,000",
        lastUpdate: "2025-10-15",
      },
      {
        distributorName: "HealthCare Distributors Pvt Ltd",
        distributorAddress: "456 Health Street, Delhi, NCR 110001",
        gstin: "07FGHIJ5678K2A6",
        contact: "+91 98234 67109",
        credit: "₹4,75,000",
        lastUpdate: "2025-10-14",
      },
      {
        distributorName: "Apollo Medical Supplies",
        distributorAddress: "789 Wellness Road, Bangalore, Karnataka 560001",
        gstin: "29KLMNO9012L3B7",
        contact: "+91 98456 12340",
        credit: "₹1,85,000",
        lastUpdate: "2025-10-16",
      },
      {
        distributorName: "Prime Pharma Solutions",
        distributorAddress: "321 Care Avenue, Hyderabad, Telangana 500001",
        gstin: "36PQRST3456M4C8",
        contact: "+91 98123 67542",
        credit: "₹3,20,000",
        lastUpdate: "2025-10-13",
      },
      {
        distributorName: "MedEx Distributors",
        distributorAddress: "654 Medicine Lane, Chennai, Tamil Nadu 600001",
        gstin: "33UVWXY7890N5D9",
        contact: "+91 99567 81234",
        credit: "₹2,70,000",
        lastUpdate: "2025-10-17",
      },
      {
        distributorName: "Global Health Partners",
        distributorAddress: "987 Pharma Complex, Pune, Maharashtra 411001",
        gstin: "27ZABCD2345O6E0",
        contact: "+91 98770 11445",
        credit: "₹5,45,000",
        lastUpdate: "2025-10-12",
      },
      {
        distributorName: "Reliable Medical Corp",
        distributorAddress: "147 Healthcare Hub, Ahmedabad, Gujarat 380001",
        gstin: "24EFGHI6789P7F1",
        contact: "+91 98122 44356",
        credit: "₹3,90,000",
        lastUpdate: "2025-10-16",
      },
      {
        distributorName: "Unity Pharmaceutical Dist",
        distributorAddress: "258 Medical Center, Kolkata, West Bengal 700001",
        gstin: "19JKLMN0123Q8G2",
        contact: "+91 98345 78901",
        credit: "₹2,15,000",
        lastUpdate: "2025-10-15",
      },
      {
        distributorName: "Supreme Medical Supplies",
        distributorAddress: "369 Health District, Jaipur, Rajasthan 302001",
        gstin: "08OPQRS4567R9H3",
        contact: "+91 98453 66543",
        credit: "₹4,25,000",
        lastUpdate: "2025-10-14",
      },
      {
        distributorName: "Elite Pharma Network",
        distributorAddress: "741 Medicine Square, Lucknow, Uttar Pradesh 226001",
        gstin: "09TUVWX8901S0I4",
        contact: "+91 98876 99012",
        credit: "₹3,05,000",
        lastUpdate: "2025-10-17",
      },
    ],
    []
  );

  const onGridReady = useCallback((params) => {
    const dataSource = {
      rowCount: undefined,
      getRows: (getRowsParams) => {
        const { startRow, endRow } = getRowsParams;
        const rowsThisPage = staticRows.slice(startRow, endRow);
        const lastRow =
          endRow >= staticRows.length ? staticRows.length : -1;

        if (typeof setHasData === "function") {
          setHasData(staticRows.length > 0);
        }

        getRowsParams.successCallback(rowsThisPage, lastRow);
      },
    };

    params.api.setGridOption("datasource", dataSource);
  }, [setHasData, staticRows]);

  // Updated column definitions to match the image structure
  const [colDefs] = useState([
    {
      headerName: "DISTRIBUTOR/ ADDRESS",
      field: "distributorName",
      width: 400,
      minWidth: 400,
      suppressSizeToFit: true,
      sortable: true,
      filter: true,
      cellRenderer: function (params) {
        if (params.value !== undefined && params.data) {
          const name = params.value || "U";
          const initial = name.charAt(0).toUpperCase();
          const bgColor = getStableColor(name);
          const address = params.data.distributorAddress || "";
          
          return (
            <div className="d-flex align-items-center gap-3">
              <div
                className={`d-flex justify-content-center align-items-center rounded-circle text-white fw-bold ${bgColor}`}
                style={{ width: "2rem", height: "2rem", fontSize: "0.875rem" }}
              >
                {initial}
              </div>
              <div className="d-flex flex-column ">
                <span className="fw-bold text-secondary small">{name}</span>
                <span className="text-secondary fw-medium small" style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>
                  {address}
                </span>
              </div>
            </div>
          );
        }
      },
    },
    {
      headerName: "GSTIN",
      field: "gstin",
      width: 180,
      minWidth: 180,
      suppressSizeToFit: true,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (params.value !== undefined) {
          return (
            <span className="text-secondary fw-medium small">{params.value}</span>
          );
        }
      },
    },
    {
      headerName: "CONTACT",
      field: "contact",
      width: 150,
      minWidth: 150,
      suppressSizeToFit: true,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (params.value !== undefined) {
          return (
            <span className="text-secondary fw-medium small">{params.value}</span>
          );
        }
      },
    },
    {
      headerName: "CREDIT",
      field: "credit",
      width: 140,
      minWidth: 140,
      suppressSizeToFit: true,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (params.value !== undefined) {
          return (
            <span className="text-secondary fw-medium small">{params.value}</span>
          );
        }
      },
    },
    {
      headerName: "LAST UPDATE",
      field: "lastUpdate",
      width: 140,
      minWidth: 140,
      suppressSizeToFit: true,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
  if (params.value !== undefined) {
    const date = new Date(params.value);

    // Extract parts manually
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    // Format: 20-Dec-2025
    const formattedDate = `${day}-${month}-${year}`;

    return (
      <span className="text-secondary fw-medium small">{formattedDate}</span>
    );
  }
}

    },
    {
      headerName: "Action",
      field: "",
      minWidth: 120,
      suppressSizeToFit: true,
      resizable: false,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      cellRenderer: (params) => {
        if (params.data !== undefined) {
          return <ActionCellDistributor data={params.data} />;
        }
      },
    },
  ]);

  const autoSizeStrategy = {
    type: "fitGridWidth",
    columnLimits: [
      {
        colId: "distributorName",
        minWidth: 280,
      },
    ],
  };

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      filter: true,
      sortable: true,
      enableRowGroup: true,
      sort: "asc",
      resizable: true,
      headerComponent: CustomHeader,
    };
  }, []);

   const tableStyle = `

  .btn-IdWmkZEQJd {
  border: none;
    background: none;
    color: #035ecf;
    width: 2.4rem;
    height: 2.4rem;
    display: flex
;
    align-items: center;
    justify-content: center;
    border-radius: .245rem;
    padding: .25rem .5rem;
  }


.ag-horizontal-right-spacer.ag-scroller-corner {
    display: none !important;
}

.ag-header-viewport {
  overflow-x: hidden !important;
  will-change: auto !important;
}

/* Soothing green strip for selected row */
.ag-theme-quartz .ag-row-selected::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #77c658; /* strip stays green */
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  z-index: 1;
}

.ag-cell-value{
  border-left: unset !important;
  border-right: 1px solid #e5e5e5 !important;
}

.ag-column-first{
  border: unset !important;
}

.ag-theme-quartz .ag-row-selected::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #77c658; /* strip stays green */
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  z-index: 1;
}

/* Override hover background for selected rows */
.ag-theme-quartz .ag-row-hover .ag-row-selected:hover {
  background-color: #f4fbf1 !important; /* same as selected */
}

.ag-checkbox-input-wrapper::before{
  display: none;
}

.ag-checkbox-input-wrapper::after{
  display: none;
}

  .ag-ltr .ag-sort-indicator-icon {
    padding-left: var(--ag-grid-size);
    display: none;
}

  .ag-header, .ag-advanced-filter-header {
    // background-color: #ff525a;
      background-color: #203342;
    border-bottom: var(--ag-borders-critical) var(--ag-border-color);
}

            .ag-full-width-container{
            height : 100% !important;
          }


         

  
        .ag-header-cell-text {
        font-size: 0.9rem;
      text-transform: uppercase;
      color: #1e374f;
      font-weight: 600;
      font-family: 'Nunito', sans-serif !important;

        }

              .ag-header-cell-resize::after{
                      background-color: #1991eb52;
                  }


            .ag-theme-quartz{
            padding: 0.4rem;
            }

              .ag-ltr .ag-cell{

                      text-align: center;
                      display: flex;
                      // justify-content: center;
                      align-items: center;
              }

              .ag-header{
                  // background-color: #edeff6;
                   border: none;
                  padding: 0.5rem 0;

                  min-height: 44px !important;
                  font-family: 'Nunito', sans-serif !important;
              }

              .ag-row {
               border: 1px solid #eeedf0;

              }

                  .ag-row-odd {
    background-color: #fff !important; /* Light gray */
  }

                    .ag-row-even {
    background-color: #f7f7f7 !important; /* Light gray */
  }






              .ag-root-wrapper{
                border: unset;
                background: transparent
              }

              .ag-body-viewport{
                border: 1px solid #8080801a;
                border-top-left-radius: 5px !important;
                border-top-right-radius: 5px !important;
                background-color: white;
              }

              .ag-theme-quartz .ag-header-cell-filter-button{
              display: none;}

.ag-header-cell-resize:after {
    background-color: #1991eb52;
    content: "";
    display: block;
    height: 30%;
    position: absolute;
    top: calc(50% - 30% * .5);2px;
    z-index: 1;
}

               .ag-cell, .ag-cell-not-inline-editing, .ag-cell-normal-height, .ag-cell-value, .ag-cell-focus{
                  font-size: .9rem;
               }

        .ag-body-vertical-scroll{
            display : none;
          }

    .ag-header-cell-sortable .ag-header-cell-label{
           justify-content: start;
        }
        
        .ag-ltr .ag-cell {
            font-weight: 500 !important;
            letter-spacing: 0.5px;
            color: #2b2b2b !important;
        }


          .odd-row {
          background-color: #f8f8fc;
        }
        
        .span-saceuu7u7{
            font-size: 13px; 
            background-color: #FAAB3C; 
            padding: .8rem .5rem;
            border-radius: 10rem;
            height:1rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .span-jkmkmkmkm{
            font-size: 10px; 
            color: #6941C6 ;
            border: solid .1px #6941C6; 
            background-color: #F9F5FF; 
            padding: .8rem .5rem;
            border-radius: 10rem;
            width: 7rem;
            height:1rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .span-jkmkmkmkmjbui{
            font-size: 10px; 
            color: #175CD3 ;
            border: solid .1px #B2DDFF; 
            background-color: #EFF8FF; 
            padding: .8rem .5rem;
            border-radius: 10rem;
            width: 7rem;
            height:1rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
                .ag-header-cell-label{
        display: flex;
        justify-content: center;
        }
         .ag-cell:focus,
  .ag-cell-focus,
  .ag-row-focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }

        
        
  
  `;

  let content = null;

  if (!hasData) {
    content = (
      <div className="flex flex-col justify-center items-center h-[30rem]">
        <img
          src="/Images/doctortable.png"
          alt="No Distributors"
          className="w-[400px] mb-4"
        />
        <p className="text-muted text-sm">No Distributors Available</p>
      </div>
    );
  } else {
    content = (
      <div
        id="grid-wrapper"
        className="ag-theme-quartz custom-ag-grid"
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "#fff",
          minHeight: "32rem",
        }}
      >
        <style>{tableStyle}</style>
        <AgGridReact
          ref={gridRef}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          rowHeight={40}
          headerHeight={30}
          rowBuffer={0}
          rowModelType={"infinite"}
          cacheBlockSize={100}
          cacheOverflowSize={5}
          maxConcurrentDatasourceRequests={1}
          maxBlocksInCache={10}
          animateRows={true}
          onGridReady={onGridReady}
        />
      </div>
    );
  }

  return <div style={{ width: "100%", height: "89%" }}>{content}</div>;
}

export default DistributorTable;
